import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, buildWhatsAppLink, formatINR } from "@/lib/api";
import BookingInquiryDialog from "@/components/BookingInquiryDialog";
import { Check, X, Clock, MapPin, Loader2, MessageCircle, ChevronLeft } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function PackageDetail() {
  const { slug } = useParams();
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/packages/${slug}`)
      .then((r) => setPkg(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="flex justify-center py-32" data-testid="pkg-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[color:var(--brand-primary)]" />
      </div>
    );
  }

  if (notFound || !pkg) {
    return (
      <div className="container-narrow py-32 text-center" data-testid="pkg-not-found">
        <div className="overline">404</div>
        <h1 className="font-heading text-5xl mt-3">Package not found.</h1>
        <Link to="/packages" className="link-underline mt-6 inline-flex">
          <ChevronLeft className="w-4 h-4" /> Back to all packages
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="package-detail">
      {/* Hero */}
      <section className="relative min-h-[65vh] flex items-end">
        <img
          src={pkg.hero_image}
          alt={pkg.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative container-narrow py-16 text-white w-full">
          <Link to="/packages" className="chip chip-dark inline-flex mb-6" data-testid="back-link">
            <ChevronLeft className="w-3 h-3" /> All packages
          </Link>
          <div className="flex gap-2 mb-4">
            <span className="chip chip-dark">{pkg.category}</span>
            <span className="chip chip-dark">
              <Clock className="w-3 h-3" /> {pkg.duration_days}D / {pkg.duration_nights}N
            </span>
          </div>
          <h1 className="font-heading font-light text-5xl sm:text-6xl leading-tight max-w-3xl">
            {pkg.title}
          </h1>
          <p className="mt-4 flex items-center gap-2 text-white/85">
            <MapPin className="w-4 h-4" /> {pkg.destination}
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="container-narrow py-16 grid lg:grid-cols-3 gap-16">
        <div className="lg:col-span-2 space-y-16">
          {/* Overview */}
          <div>
            <div className="overline">Overview</div>
            <p className="mt-4 text-lg leading-relaxed text-[color:var(--brand-text)] font-heading">
              {pkg.description}
            </p>
            {pkg.highlights?.length > 0 && (
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {pkg.highlights.map((h) => (
                  <li key={h} className="flex gap-2 text-sm">
                    <Check className="w-4 h-4 text-[color:var(--brand-primary)] mt-0.5 shrink-0" />
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Itinerary */}
          {pkg.itinerary?.length > 0 && (
            <div data-testid="itinerary-section">
              <div className="overline">Day by day</div>
              <h2 className="font-heading text-3xl sm:text-4xl mt-2">The itinerary</h2>
              <Accordion type="single" collapsible className="mt-6" defaultValue="day-1">
                {pkg.itinerary.map((it) => (
                  <AccordionItem key={it.day} value={`day-${it.day}`} data-testid={`day-${it.day}`}>
                    <AccordionTrigger className="text-left">
                      <span className="flex items-center gap-4">
                        <span className="font-heading text-2xl text-[color:var(--brand-primary)]">
                          {String(it.day).padStart(2, "0")}
                        </span>
                        <span className="font-heading text-xl">{it.title}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-[color:var(--brand-text-soft)] leading-relaxed pl-14">
                      {it.description}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          {/* Inclusions/Exclusions */}
          <div className="grid md:grid-cols-2 gap-10">
            {pkg.inclusions?.length > 0 && (
              <div>
                <div className="overline">Included</div>
                <ul className="mt-4 space-y-3">
                  {pkg.inclusions.map((i) => (
                    <li key={i} className="flex gap-2 text-sm">
                      <Check className="w-4 h-4 text-[color:var(--brand-primary)] mt-0.5 shrink-0" />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {pkg.exclusions?.length > 0 && (
              <div>
                <div className="overline">Not included</div>
                <ul className="mt-4 space-y-3">
                  {pkg.exclusions.map((i) => (
                    <li key={i} className="flex gap-2 text-sm text-[color:var(--brand-text-soft)]">
                      <X className="w-4 h-4 mt-0.5 shrink-0" />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Gallery */}
          {pkg.gallery?.length > 0 && (
            <div>
              <div className="overline">Moments from this trip</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                {pkg.gallery.map((g, i) => (
                  <div key={g + i} className="img-frame aspect-square">
                    <img src={g} alt={`${pkg.title} ${i}`} loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Booking card */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 border border-[color:var(--brand-border)] bg-[color:var(--brand-surface)] p-8" data-testid="booking-sticky">
            <div className="overline">From</div>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="font-heading text-4xl">{formatINR(pkg.price_inr)}</span>
              {pkg.original_price_inr && pkg.original_price_inr > pkg.price_inr && (
                <span className="text-sm line-through text-[color:var(--brand-text-soft)]">
                  {formatINR(pkg.original_price_inr)}
                </span>
              )}
            </div>
            <p className="text-xs text-[color:var(--brand-text-soft)] mt-1">
              per person · twin sharing · excl. flights
            </p>

            <div className="rule my-6" />

            <BookingInquiryDialog
              pkg={pkg}
              trigger={
                <button className="btn-primary w-full" data-testid="detail-book-btn">
                  Send booking inquiry
                </button>
              }
            />
            <a
              href={buildWhatsAppLink(
                `Hi! I'm interested in the "${pkg.title}" package. Can you share more details?`,
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="wa-btn w-full mt-3 py-3 flex items-center justify-center gap-2 text-sm font-medium"
              data-testid="detail-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
            </a>

            <div className="rule my-6" />
            <ul className="space-y-2 text-xs text-[color:var(--brand-text-soft)]">
              <li>✓ No pre-payment to inquire</li>
              <li>✓ Custom dates & group sizes welcome</li>
              <li>✓ On-ground team, 24×7 during trip</li>
            </ul>
          </div>
        </aside>
      </section>

      {/* Mobile-only sticky action bar */}
      <div
        data-testid="pkg-sticky-mobile-bar"
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-[color:var(--brand-surface)]/95 backdrop-blur-md border-t border-[color:var(--brand-border)] shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.12)]"
      >
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--brand-text-soft)]">
              From
            </div>
            <div className="flex items-baseline gap-2">
              <span className="font-heading text-lg leading-none">
                {formatINR(pkg.price_inr)}
              </span>
              {pkg.original_price_inr && pkg.original_price_inr > pkg.price_inr && (
                <span className="text-[10px] line-through text-[color:var(--brand-text-soft)]">
                  {formatINR(pkg.original_price_inr)}
                </span>
              )}
            </div>
          </div>
          <a
            href={buildWhatsAppLink(
              `Hi! I'm interested in the "${pkg.title}" package. Can you share more details?`,
            )}
            target="_blank"
            rel="noopener noreferrer"
            className="wa-btn rounded-full p-3"
            data-testid="pkg-sticky-whatsapp"
            aria-label="Chat on WhatsApp"
          >
            <MessageCircle className="w-5 h-5" strokeWidth={1.7} />
          </a>
          <BookingInquiryDialog
            pkg={pkg}
            trigger={
              <button
                className="btn-primary text-sm px-4 py-2.5"
                data-testid="pkg-sticky-inquire"
              >
                Enquire
              </button>
            }
          />
        </div>
      </div>

      {/* Bottom safe-area so the mobile sticky bar doesn't hide content */}
      <div className="lg:hidden h-20" aria-hidden="true" />
    </div>
  );
}
